import {
  Box,
  Chip,
  CircularProgress,
  Divider,
  List,
  ListItem,
  Stack,
  Typography,
} from '@mui/material';
import { IAISystem } from 'modules/ai-inventory/ai-system/components/AISystemRecorder';
import { IDataset } from 'modules/ai-inventory/datasets/records-dataset/Main';
import { useEffect, useState } from 'react';
import { DatasetsService } from 'services';
import {
  AppPrimaryButton,
  AppPrimaryButtonOutlined,
  AppSelectWithKeys,
} from 'shared/components/AppComponents';
import LaunchIcon from '@mui/icons-material/Launch';
import { Link } from 'react-router-dom';
import { VulnerabilityService } from 'services/VulnerabilityService';
import { useSelector } from 'react-redux';
import { IAppState } from 'state/RootReducer';

interface ILinkedDatasetsProps {
  linkedDatasets: IDataset[];
  vulnerabilityId: string;
  reloadDetail: () => void;
}

const LinkedDatasets: React.FC<ILinkedDatasetsProps> = (props) => {
  const { linkedDatasets, vulnerabilityId, reloadDetail } = props;
  const [isLoadingRecords, setIsLoadingRecords] = useState(false);
  const [records, setRecords] = useState<IAISystem[]>([]);
  const [selectedRecordId, setSelectedRecordId] = useState('');
  const [isLinking, setIsLinking] = useState('');
  const { orgACLConfig } = useSelector(
    (state: IAppState) => state.baseAppState
  );

  // API Calls
  const loadDatasets = (): void => {
    setIsLoadingRecords(true);
    const queryString = new URLSearchParams({
      offset: '0',
      limit: '1000',
      search: '',
    }).toString();
    DatasetsService.getDatasets(queryString)
      .then((response) => {
        const { datasets } = response.data.data;
        /* eslint no-underscore-dangle: 0 */
        setRecords(datasets.map((d: any) => ({ ...d, id: d._id })));
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => {
        setIsLoadingRecords(false);
      });
  };

  const linkRecord = (payload: any, callback: any): void => {
    VulnerabilityService.linkDataset(vulnerabilityId, payload)
      .then((response) => {
        callback(response);
      })
      .catch(() => {
        callback(false);
      });
  };

  // UI Actions
  const actionOnLink = (status: string, id: string): void => {
    setIsLinking(status);
    linkRecord(
      {
        datasetId: id,
        action: status,
      },
      (response: any) => {
        setIsLinking('');
        setSelectedRecordId('');
        if (typeof response === 'object') {
          reloadDetail();
        }
      }
    );
  };

  // Hooks
  useEffect(() => {
    loadDatasets();
  }, []);

  // UI Elements
  return (
    <Box className="app-plain-card">
      <Box className="title">
        <Stack spacing={2} direction="row" alignItems="center">
          <img height={20} src="/images/icons/IconDb.svg" />
          <Typography variant="h4">Datasets</Typography>
        </Stack>
        <Typography className="text-gray">
          Datasets affected by this vulnerability
        </Typography>
      </Box>

      <Box className="content">
        {isLoadingRecords && (
          <Box className="empty-content">
            <CircularProgress />
          </Box>
        )}

        {!isLoadingRecords && (
          <>
            {orgACLConfig.vulnerabilities?.includes(
              'vulnerabilities.aiVulnerabilities.dataset.link'
            ) && (
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={{ xs: 1.5, sm: 4 }}
                alignItems={{ xs: 'stretch', sm: 'center' }}
                sx={{ width: '100%' }}
              >
                <AppSelectWithKeys
                  className
                  label=""
                  onChange={(e: any) => {
                    setSelectedRecordId(e.target.value);
                  }}
                  items={records.filter(
                    (r) => !linkedDatasets.some((ls) => ls._id === r._id)
                  )}
                  titleKey="datasetName"
                  valueKey="_id"
                />
                <AppPrimaryButtonOutlined
                  isLoading={isLinking === 'link'}
                  disabled={!selectedRecordId}
                  title="Link"
                  onClick={() => {
                    actionOnLink('link', selectedRecordId);
                  }}
                />
              </Stack>
            )}
            {linkedDatasets.length > 0 && (
              <List className="app-box-border mt-4">
                {linkedDatasets.map((dataset, index) => (
                  <>
                    <ListItem>
                      <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        justifyContent="space-between"
                        alignItems={{ xs: 'flex-start', sm: 'center' }}
                        spacing={{ xs: 1.5, sm: 0 }}
                        sx={{ width: '100%' }}
                      >
                        <Stack>
                          <Link to={`/datasets/records/${dataset._id}`}>
                            <Stack
                              className="text-primary mb-2"
                              alignItems="center"
                              direction="row"
                              spacing={2}
                            >
                              <LaunchIcon />
                              <Typography className="f18 fw-6">
                                {dataset.datasetName}
                              </Typography>
                            </Stack>
                          </Link>
                          <Typography
                            variant="body1"
                            className="text-gray  mb-2"
                          >
                            {dataset.purposeOfUse}
                          </Typography>
                          <Stack direction="row" spacing={2}>
                            <Chip size="small" label={dataset.sourceType} />
                            {dataset.lifecycleStage && (
                              <Chip
                                size="small"
                                label={dataset.lifecycleStage}
                              />
                            )}
                          </Stack>
                        </Stack>
                        {orgACLConfig.vulnerabilities?.includes(
                          'vulnerabilities.aiVulnerabilities.dataset.unlink'
                        ) && (
                          <AppPrimaryButton
                            isLoading={selectedRecordId === dataset._id}
                            onClick={() => {
                              setSelectedRecordId(dataset._id);
                              actionOnLink('unlink', dataset._id);
                            }}
                            className="btn-danger btn-extra-small"
                            title="Unlink"
                          />
                        )}
                      </Stack>
                    </ListItem>
                    {index < linkedDatasets.length - 1 && <Divider />}
                  </>
                ))}
              </List>
            )}
          </>
        )}

        {!isLoadingRecords && linkedDatasets.length === 0 && (
          <Box className="empty-content">
            <Typography className="f18">
              No datasets linked to this vulnerability
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default LinkedDatasets;
